import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import {
  fetchSuggestedTasks,
} from '../../../store/actions/Matters/MatterActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { fetchAllTasks, completeTask, archiveTask, updateTask, addTaskComment } from '../../../store/actions/TasksActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import ListRow, { MetaDot, RowGlyph } from '../../shared/surface/ListRow';
import { useMatterSurface } from '../surface/MatterSurfaceContext';
import {
  priorityFlagClass,
  suggestionId,
  taskPriorityLabel,
} from '../tasks/taskConstants';
import MatterTasksTable from '../tasks/MatterTasksTable';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import dayjs from 'dayjs';
import { matterTaskQuery } from './matterTaskQuery';
import {
  PiCheckSquareLight,
  PiLightbulbLight,
  PiEyeLight,
  PiEyeSlashLight,
  PiPlusLight,
  PiCheckCircleLight,
  PiFlagFill,
  PiArchiveLight,
  PiSparkleLight,
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import clsx from 'clsx';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, tasksItemsTourSteps } from '../../shared/FeatureTour';

export interface SuggestedTaskItem {
  TaskTitle: string;
  TaskDescription: string;
  TaskDeadline: string;
  Priority?: string;
  MatterID?: string;
  CaseID?: string;
  selected?: boolean;
}

interface TasksTabContentProps {
  matterData: MatterData | null;
  suggestedTasks: SuggestedTaskItem[];
  setSuggestedTasks: React.Dispatch<React.SetStateAction<SuggestedTaskItem[]>>;
  matterTasks: any[];
  setMatterTasks: React.Dispatch<React.SetStateAction<any[]>>;
  suggestedTasksLoading: boolean;
  matterTasksLoading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

type TasksSubTab = 'suggested' | 'active';

const TasksTabContent: React.FC<TasksTabContentProps> = ({
  matterData,
  suggestedTasks,
  setSuggestedTasks,
  matterTasks,
  setMatterTasks,
  suggestedTasksLoading,
  matterTasksLoading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { matterId } = useParams<{ matterId: string }>();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const surface = useMatterSurface();

  /** Add → the shared container, empty, in create mode. */
  const openCreateTask = () => {
    if (!canEdit) return;
    surface?.select({ kind: 'task', id: null, mode: 'create' });
  };

  const matterUuid = matterData?.uuid || matterData?.MatterID || matterId || '';

  const [subTab, setSubTab] = useState<TasksSubTab>('suggested');
  const [searchTerm, setSearchTerm] = useState('');

  /*
    Follow the selection to its list.

    Something outside this tab can now select an item - the Overview's "Next
    steps" rows link straight to a task or a suggestion. Landing on the tab
    with the detail container open on an item that is not in the visible list
    reads as a bug, so the sub-tab moves to wherever the selected item lives.
    Keyed on the selection's identity, so a manual sub-tab switch afterwards
    is not immediately undone.
  */
  const selectedKind = surface?.selection?.kind;
  const selectedId = surface?.selection?.id;
  useEffect(() => {
    if (selectedKind === 'suggestion') setSubTab('suggested');
    else if (selectedKind === 'task') setSubTab('active');
  }, [selectedKind, selectedId]);
  const [isTabLoading, setIsTabLoading] = useState(false);
  /*
    "Active Tasks" used to fetch with NO status filter, so the tab named for
    active work listed completed and archived tasks alongside them - while the
    Tasks page's own "active" means todo + in_progress. The list now matches
    the app-wide filter, and this toggle brings the closed ones back on demand
    so nothing is hidden for good and the row's Restore action still has
    something to act on.
  */
  const [showClosed, setShowClosed] = useState(false);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const { isGenerating: isFindingMoreTasks, handleGenerateMore: handleFindMoreTasks, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'tasks',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'tasks-items',
    steps: tasksItemsTourSteps,
    readyToShow: (suggestedTasks.length > 0 || matterTasks.length > 0) && !suggestedTasksLoading && !matterTasksLoading,
    autoStartDelay: 600,
  });

  // Editable local copy of suggested tasks
  const [editableTasks, setEditableTasks] = useState<SuggestedTaskItem[]>([]);

  useEffect(() => {
    setEditableTasks(
      suggestedTasks.map((task) => ({ ...task, selected: true }))
    );
  }, [suggestedTasks]);

  const refreshSuggestedTasks = useCallback(async () => {
    if (!matterUuid) return;
    setIsTabLoading(true);
    try {
      const tasks = await dispatch(fetchSuggestedTasks(matterUuid));
      setSuggestedTasks(tasks || []);
    } catch (error) {
      console.error('Failed to load suggested tasks:', error);
    } finally {
      setIsTabLoading(false);
    }
  }, [matterUuid, dispatch, setSuggestedTasks]);

  const refreshMatterTasks = useCallback(async () => {
    if (!matterUuid) return;
    setIsTabLoading(true);
    try {
      const result = await dispatch(fetchAllTasks(matterTaskQuery(matterUuid, showClosed)));
      setMatterTasks(result?.tasks || []);
    } catch (error) {
      console.error('Failed to load matter tasks:', error);
    } finally {
      setIsTabLoading(false);
    }
  }, [matterUuid, dispatch, setMatterTasks, showClosed]);

  /* Flipping the toggle changes the QUERY, not a client-side filter - the
     closed tasks were never fetched - so it has to go back to the server. */
  useEffect(() => {
    if (subTab === 'active') refreshMatterTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClosed]);

  // --- Sub-tab definitions (matches Sources/Q&As pattern) ---

  const taskTabs = useMemo(() => [
    {
      id: 'suggested' as TasksSubTab,
      label: t('Suggestions'),
      icon: <PiLightbulbLight className="h-3 w-3" />,
      count: suggestedTasks.length,
      color: 'yellow' as BadgeColor,
    },
    {
      id: 'active' as TasksSubTab,
      label: t('Active tasks'),
      icon: <PiCheckSquareLight className="h-3 w-3" />,
      count: matterTasks.length,
      color: 'blue' as BadgeColor,
    },
  ], [t, suggestedTasks.length, matterTasks.length]);

  // --- Suggested Tasks handlers ---

  // --- Active Tasks action handlers ---

  const handleCompleteTask = async (taskUuid: string) => {
    dispatch(
      showModal({
        type: 'addTaskComment',
        message: t('Complete task'),
        icon: <PiCheckCircleLight className="w-6 h-6 text-success-600" />,
        subMsg: t('Add an optional comment to let others know about the completion.'),
        onConfirm: async (comment: string) => {
          try {
            await dispatch(completeTask(taskUuid) as any);
            if (comment?.trim()) {
              await dispatch(addTaskComment(taskUuid, comment) as any);
            }
            await refreshMatterTasks();
            onUpdate();
            dispatch(hideModal());
          } catch (error) {
            dispatch(showFeedbackModal({ modalType: 'error', message: t('Error completing task.'), showModal: true, duration: 3000 }));
          }
        },
        showModal: true,
        onCancel: () => dispatch(hideModal()),
      } as any)
    );
  };

  const handleArchiveTask = async (taskUuid: string) => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Archive task'),
        subMsg: t('This task will be archived. You can restore it later if needed.'),
        icon: <PiArchiveLight className="w-6 h-6 text-danger-600" />,
        onConfirm: async () => {
          try {
            await dispatch(archiveTask(taskUuid) as any);
            await refreshMatterTasks();
            onUpdate();
            dispatch(hideModal());
          } catch (error) {
            dispatch(showFeedbackModal({ modalType: 'error', message: t('Error archiving task.'), showModal: true, duration: 3000 }));
            dispatch(hideModal());
          }
        },
      })
    );
  };

  const handleRestoreTask = async (taskUuid: string) => {
    try {
      await dispatch(updateTask({ TaskID: taskUuid, Status: 'todo' }) as any);
      dispatch(showFeedbackModal({ modalType: 'success', message: t('Task restored.'), showModal: true, duration: 3000 }));
      await refreshMatterTasks();
      onUpdate();
    } catch (error) {
      dispatch(showFeedbackModal({ modalType: 'error', message: t('Error restoring task.'), showModal: true, duration: 3000 }));
    }
  };

  // --- Active Tasks helpers ---


  // --- Filtering ---

  const filteredSuggestedTasks = useMemo(() =>
    editableTasks.filter(
      (task) =>
        !searchTerm ||
        task.TaskTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.TaskDescription?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [editableTasks, searchTerm]
  );

  const filteredMatterTasks = useMemo(() =>
    matterTasks.filter(
      (task) =>
        !searchTerm ||
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [matterTasks, searchTerm]
  );

  // --- Priority options (matches TaskForm) ---

  // --- Loading states (content only, tab bar stays visible) ---

  const isContentLoading =
    (subTab === 'suggested' && (suggestedTasksLoading || isTabLoading)) ||
    (subTab === 'active' && (matterTasksLoading || isTabLoading));

  // --- Render content skeleton (inside layout, tab bar stays) ---

  const renderContentSkeleton = () => (
    <div className="py-2">
      <Skeleton variant="table" rows={5} />
    </div>
  );

  // --- Render suggested tasks content ---

  const renderSuggestedContent = () => {
    if (editableTasks.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
            <PiLightbulbLight className="h-6 w-6 text-champagne" />
          </div>
          <h3 className="text-fg-body font-medium mb-1">{t('No suggested tasks')}</h3>
          <p className="text-sm text-fg-muted max-w-md">
            {t('Task suggestions appear here once the matter has been analysed.')}
          </p>
          {canEdit && (
            <div className="flex items-center gap-2 mt-4">
              <Button variant="outline" size="small" onClick={openCreateTask}>
                {t('Add task')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleFindMoreTasks()}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (filteredSuggestedTasks.length === 0) {
      return (
        <p className="text-sm text-fg-muted text-center py-8">
          {t('No suggestions match your search.')}
        </p>
      );
    }

    return (
      <GlassCard padding="none">
        <div className="divide-y divide-hairline-soft">
          {filteredSuggestedTasks.map((task, index) => {
            const id = suggestionId(task, editableTasks.indexOf(task));
            const due = task.TaskDeadline || (task as any).due_date || '';
            return (
              <ListRow
                key={id}
                data-testid="suggestion-row"
                index={index}
                selected={surface?.selection?.kind === 'suggestion' && surface.selection.id === id}
                onClick={() => surface?.select({ kind: 'suggestion', id, mode: 'edit' })}
                title_={task.TaskTitle}
                /* Dashed: a suggestion is not a task until it is accepted. */
                glyph={<RowGlyph state="pending"><PiLightbulbLight className="h-3.5 w-3.5" /></RowGlyph>}
                title={task.TaskTitle || t('Untitled task')}
                meta={(
                  <>
                    <span>{t('Suggested')}</span>
                    {task.TaskDescription && (<><MetaDot /><span className="truncate">{task.TaskDescription}</span></>)}
                    {due && (<><MetaDot /><span>{dayjs(due).format('MMM DD, YYYY')}</span></>)}
                  </>
                )}
                rightWidth="w-[80px]"
                right={(
                  <PiFlagFill
                    className={clsx('w-3.5 h-3.5', priorityFlagClass(task.Priority))}
                    aria-label={t(taskPriorityLabel(task.Priority))}
                  />
                )}
              />
            );
          })}
        </div>
      </GlassCard>
    );
  };

  const canUserManageTask = (task: any): boolean => {
    if (!task || !userInfo) return false;
    const isCreator = task.created_by_user_id === userInfo.UserID;
    const isAssignee = task.assigned_to_user_id === userInfo.UserID || task.UserID === userInfo.UserID;
    if (isCreator || isAssignee) return true;
    if (canEdit) return true;
    return false;
  };


  if (!matterData) {
    return (
      <div className="p-6 text-center text-fg-muted">
        {t('Loading tasks...')}
      </div>
    );
  }

  /**
   * The active tasks, as a TABLE.
   *
   * A task is a record with five facts worth comparing down a page - what, who,
   * when, how urgent, where it is up to - so it gets five columns rather than
   * one run-on meta line and an unlabelled flag at the right edge. Clicking a
   * row still SELECTS, and the shared detail container still does the editing;
   * Complete / Archive / Restore move into the table's own actions menu.
   */
  const renderActiveContent = () => {
    if (filteredMatterTasks.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
            <PiCheckSquareLight className="h-6 w-6 text-champagne" />
          </div>
          <h3 className="text-fg-body font-medium mb-1">
            {searchTerm
              ? t('No matching tasks')
              : showClosed ? t('No tasks yet') : t('No open tasks')}
          </h3>
          <p className="text-sm text-fg-muted max-w-md">
            {searchTerm
              ? t('Try adjusting your search.')
              : showClosed
                ? t('Tasks linked to this matter appear here. Accept a suggestion, or create one from the Tasks page.')
                /* Empty here does not mean empty full stop - completed and
                   archived tasks are simply not in this query. */
                : t('Nothing open on this matter. Completed and archived tasks are hidden — use Show completed to see them.')}
          </p>
        </div>
      );
    }

    return (
      <MatterTasksTable
        tasks={filteredMatterTasks}
        selectedId={surface?.selection?.kind === 'task' ? surface.selection.id : null}
        onSelectTask={(id) => surface?.select({ kind: 'task', id, mode: 'edit' })}
        canManage={canUserManageTask}
        onComplete={handleCompleteTask}
        onArchive={handleArchiveTask}
        onRestore={handleRestoreTask}
      />
    );
  };

  return (
    <div className="space-y-2">
      <div data-tour-id="tasks-sub-tabs">
      <SubTabBar
        tabs={taskTabs}
        activeTab={subTab}
        onTabChange={(id) => {
          setSubTab(id as TasksSubTab);
          setSearchTerm('');
          if (id === 'active' && matterTasks.length === 0) refreshMatterTasks();
        }}
        actions={subTab === 'active' ? (
          <Button
            variant="outline"
            size="small"
            onClick={() => setShowClosed((v) => !v)}
            icon={showClosed
              ? <PiEyeSlashLight className="h-3.5 w-3.5" />
              : <PiEyeLight className="h-3.5 w-3.5" />}
          >
            {showClosed ? t('Hide completed') : t('Show completed')}
          </Button>
        ) : canEdit && subTab === 'suggested' && !isFindingMoreTasks ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={openCreateTask}
            >
              <PiPlusLight className="h-4 w-4 me-1" />
              {t('Add task')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreTasks}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {suggestedTasks.length === 0 ? t('Generate') : t('Find more')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {/* Search + content area */}
      {isFindingMoreTasks || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMoreTasks} />
      ) : (
      <div>
        <FilterBarWithTabs
          onSearchChange={setSearchTerm}
          noParentPadding={true}
          noParentHeight={true}
          itemCount={subTab === 'suggested' ? filteredSuggestedTasks.length : filteredMatterTasks.length}
          totalItemCount={subTab === 'suggested' ? editableTasks.length : matterTasks.length}
          itemCountLabel={subTab === 'suggested'
            ? (editableTasks.length === 1 ? 'suggestion' : 'suggestions')
            : (matterTasks.length === 1 ? 'task' : 'tasks')
          }
        />

        <div data-tour-id="tasks-content-area" className="min-h-[320px]">
          {isContentLoading
            ? renderContentSkeleton()
            : subTab === 'suggested'
              ? renderSuggestedContent()
              : renderActiveContent()
          }
        </div>
      </div>
      )}
      <FeatureTour
        steps={tasksItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default TasksTabContent;
